import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { User, Mail, Phone, MapPin, Lock } from 'lucide-react';
import { Button, useToast } from '../components/ui';
import { useRegisterMutation } from '../store/customersApi';

export default function Registro() {
  const navigate = useNavigate();
  const toast = useToast();
  const [register, { isLoading }] = useRegisterMutation();

  const [form, setForm] = useState({
    name: '', email: '', phone: '', password: '',
    address: '', addressLocality: '', addressProvince: '', addressPostalCode: '',
  });

  const set = (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.warning({ title: 'Contraseña muy corta', description: 'Debe tener al menos 8 caracteres.' });
      return;
    }
    try {
      await register(form).unwrap();
      toast.success({ title: '¡Cuenta creada!', description: 'Ya iniciaste sesión.' });
      navigate('/mi-cuenta');
    } catch (e: any) {
      toast.error({ title: 'No se pudo crear la cuenta', description: e?.data?.message ?? 'Intentá de nuevo.' });
    }
  };

  const inputClass = 'w-full bg-slate-50 border border-slate-200 focus:border-indigo-500/50 focus:bg-white rounded-xl pl-10 pr-4 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none transition-colors';

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-16">
      <form onSubmit={handleSubmit} className="w-full max-w-md bg-white border border-slate-200 rounded-2xl shadow-sm p-8 space-y-4">
        <h1 className="font-heading font-extrabold text-2xl text-slate-900 text-center mb-2">Crear cuenta</h1>

        <div className="relative">
          <input required placeholder="Nombre completo" value={form.name} onChange={set('name')} className={inputClass} />
          <User className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
        </div>
        <div className="relative">
          <input required type="email" placeholder="Email" value={form.email} onChange={set('email')} className={inputClass} />
          <Mail className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
        </div>
        <div className="relative">
          <input required placeholder="Teléfono" value={form.phone} onChange={set('phone')} className={inputClass} />
          <Phone className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
        </div>
        <div className="relative">
          <input required placeholder="Dirección (calle y altura)" value={form.address} onChange={set('address')} className={inputClass} />
          <MapPin className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <input required placeholder="Localidad" value={form.addressLocality} onChange={set('addressLocality')} className={inputClass.replace('pl-10', 'px-4')} />
          <input required placeholder="Provincia" value={form.addressProvince} onChange={set('addressProvince')} className={inputClass.replace('pl-10', 'px-4')} />
        </div>
        <input required placeholder="Código postal" value={form.addressPostalCode} onChange={set('addressPostalCode')} className={inputClass.replace('pl-10', 'px-4')} />
        <div className="relative">
          <input required type="password" placeholder="Contraseña (mínimo 8 caracteres)" value={form.password} onChange={set('password')} className={inputClass} />
          <Lock className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
        </div>

        <Button type="submit" variant="primary" size="lg" loading={isLoading} className="w-full mt-2">
          Crear cuenta
        </Button>

        <p className="text-xs text-slate-500 text-center pt-2">
          ¿Ya tenés cuenta? <Link to="/login" className="text-indigo-600 font-semibold hover:underline">Iniciá sesión</Link>
        </p>
      </form>
    </div>
  );
}
